import { type RefObject, useEffect, useState } from "react";
import { GTC_SCROLL_END_THRESHOLD_PX, GTC_SCROLL_ROOT_ID } from "constants/gtc";
import { hasScrolledToDocumentEnd } from "helpers/hasScrolledToDocumentEnd";

export const useReachedDocumentEnd = (
  sentinelRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  rootId: string = GTC_SCROLL_ROOT_ID,
): boolean => {
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setHasReachedEnd(false);
      return;
    }

    let cancelled = false;
    let raf = 0;
    let detachListeners: (() => void) | undefined;

    const tryAttach = () => {
      if (cancelled) {
        return;
      }

      const root = document.getElementById(rootId);
      if (!root) {
        raf = window.requestAnimationFrame(tryAttach);
        return;
      }

      const markIfEnded = () => {
        if (hasScrolledToDocumentEnd(root, GTC_SCROLL_END_THRESHOLD_PX)) {
          setHasReachedEnd(true);
        }
      };
      markIfEnded();

      const onScroll = () => markIfEnded();
      root.addEventListener("scroll", onScroll, { passive: true });

      const sentinel = sentinelRef.current;
      let observer: IntersectionObserver | undefined;
      if (sentinel && typeof IntersectionObserver !== "undefined") {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              setHasReachedEnd(true);
            }
          },
          { root, threshold: 0.01 },
        );
        observer.observe(sentinel);
      }

      detachListeners = () => {
        root.removeEventListener("scroll", onScroll);
        observer?.disconnect();
      };
    };

    tryAttach();

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      detachListeners?.();
    };
  }, [enabled, rootId, sentinelRef]);

  return hasReachedEnd;
};
